import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Drawer, EmptyState, EyeIcon, Field, LoadingState, PlusIcon, SaveState, useConfirm, useToast, type SaveStatus } from "@signet/ui";
import { api, describeDropped, MatrixEditor, startViewAs, useMe, type Matrix, type ScopedMatrix } from "@signet/web-kit";

/* Roles: named sets of permissions, so twenty people in marketing are one
   decision rather than twenty. */

type Role = { id: string; name: string; description: string; members: number; matrix: Matrix; scoped: ScopedMatrix };

export function RolesPage() {
  const { can } = useMe();
  const roles = useQuery({ queryKey: ["roles"], queryFn: () => api<{ roles: Role[] }>("/api/admin/roles") });
  const [open, setOpen] = useState<Role | "new" | null>(null);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Roles</h1>
          <p>A role is a set of permissions you give to many people at once. Changing a role changes it for everyone in it.</p>
        </div>
        <div className="page-head-actions">
          {can("signet.roles:create") ? <button type="button" className="button brass" onClick={() => setOpen("new")}><PlusIcon size={14} />New role</button> : null}
        </div>
      </div>
      {roles.isLoading ? <LoadingState /> : roles.data?.roles.length ? (
        <div className="role-grid">
          {roles.data.roles.map((role) => (
            <button type="button" key={role.id} className="panel role-card" onClick={() => setOpen(role)}>
              <strong>{role.name}</strong>
              <p className="faint">{role.description || "No description"}</p>
              <small className="muted">{role.members === 1 ? "1 person" : `${role.members} people`}, {Object.keys(role.matrix).length} permissions</small>
            </button>
          ))}
        </div>
      ) : (
        <EmptyState title="No roles yet" body="Make one for each kind of job, like Marketing or Manager, then add people to it." />
      )}
      {open ? <RoleDrawer role={open === "new" ? null : open} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}

function RoleDrawer({ role, onClose }: { role: Role | null; onClose(): void }) {
  const { me, can } = useMe();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const queryClient = useQueryClient();
  const [name, setName] = useState(role?.name ?? "");
  const [description, setDescription] = useState(role?.description ?? "");
  const [matrix, setMatrix] = useState<Matrix>(role?.matrix ?? {});
  const [scoped, setScoped] = useState<ScopedMatrix>(role?.scoped ?? {});
  const [status, setStatus] = useState<SaveStatus>("idle");
  useEffect(() => setStatus("idle"), [role?.id]);
  const editable = role ? can("signet.roles:edit") : can("signet.roles:create");

  async function save() {
    setStatus("saving");
    try {
      let dropped: Matrix;
      if (role) {
        await api(`/api/admin/roles/${role.id}`, { method: "PATCH", body: { name, description } });
        dropped = (await api<{ dropped: Matrix }>(`/api/admin/roles/${role.id}/grants`, { method: "PUT", body: { matrix, scoped } })).dropped;
      } else {
        const made = await api<{ id: string; dropped: Matrix }>("/api/admin/roles", { method: "POST", body: { name, description, matrix } });
        dropped = made.dropped;
        if (Object.keys(scoped).length) await api(`/api/admin/roles/${made.id}/grants`, { method: "PUT", body: { matrix, scoped } });
      }
      setStatus("saved");
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      const text = describeDropped(dropped, me?.resources ?? []);
      if (text) toast.push({ tone: "info", title: "Saved, with some left out", body: `You can only give what you have yourself. Left out: ${text}` });
      if (!role) onClose();
    } catch (error) {
      setStatus("failed");
      toast.push({ tone: "bad", title: "Couldn't save", body: (error as Error).message });
    }
  }

  async function remove() {
    if (!role || !(await confirm({ title: `Delete ${role.name}?`, body: `${role.members} people lose the permissions this role gave them.`, confirmLabel: "Delete role", danger: true }))) return;
    await api(`/api/admin/roles/${role.id}`, { method: "DELETE" });
    queryClient.invalidateQueries({ queryKey: ["roles"] });
    onClose();
  }

  return (
    <Drawer
      open
      narrow
      onClose={onClose}
      title={role ? role.name : "New role"}
      footer={editable ? (
        <>
          <SaveState status={status} />
          {role && can("signet.roles:delete") ? <button type="button" className="text-button danger" onClick={remove}>Delete role</button> : null}
          <div className="spacer" />
          <button type="button" className="button brass" disabled={!name.trim() || status === "saving"} onClick={save}>{role ? "Save role" : "Create role"}</button>
        </>
      ) : null}
    >
      {element}
      <div className="signet-drawer">
      {role && me?.user.isOwner ? (
        <button type="button" className="secondary-button signet-view-as-button" onClick={() => startViewAs({ kind: "role", id: role.id, label: role.name })}>
          <EyeIcon size={15} />View Signet as {role.name}
        </button>
      ) : null}
      <Field label="Name"><input value={name} disabled={!editable} onChange={(e) => { setName(e.target.value); setStatus("dirty"); }} /></Field>
      <Field label="What it's for"><input value={description} disabled={!editable} onChange={(e) => { setDescription(e.target.value); setStatus("dirty"); }} /></Field>
      <div className="signet-gap" />
      <MatrixEditor
        resources={me?.resources ?? []}
        value={matrix}
        grantable={me?.user.isOwner ? "all" : me?.matrix ?? {}}
        readOnly={!editable}
        onChange={(next) => { setMatrix(next); setStatus("dirty"); }}
        scoped={scoped}
        grantableScoped={me?.scoped}
        onScopedChange={(next) => { setScoped(next); setStatus("dirty"); }}
      />
      </div>
    </Drawer>
  );
}
